import { Component, Input, OnInit } from '@angular/core';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ModalViewKeyComponent } from 'src/app/pages/pengaturan/pages/profile/components/modal-view-key/modal-view-key.component';
import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { BaseService } from 'src/app/services/base.service';
import { ProfileService } from 'src/app/services/profile.service';
import {
  LIST_APIKEY,
  LIST_APIKEY_COST,
  LIST_APIKEY_DELIVERY,
  LIST_APIKEY_PAYMENT,
  QRISLY_API_LIST,
  SHOW_APICOST,
  SHOW_APIDELIVERY,
} from 'src/app/constants/endpoint.const';
import { Clipboard } from '@angular/cdk/clipboard';
import { DecimalPipe } from '@angular/common';
import { environment } from 'src/environments/environment';
import { HttpHeaders, HttpContext, HttpClient } from '@angular/common/http';
import { BypassInterceptorContext } from 'src/app/interceptors/http.interceptor';
import { ApiKeyDataList, listApiKeyData } from 'src/app/types/response/settings.response.type';

/** Generic structure for API responses */
interface ApiResponse<T> {
  data: T;
  message?: string;
  status?: string | number;
}

@Component({
  standalone: false,
  selector: 'app-api-key',
  templateUrl: './api-key.component.html',
  styleUrls: ['./api-key.component.scss'],
  animations: [
    trigger('collapseExpand', [
      state(
        'collapsed',
        style({
          height: '0px',
          opacity: 0,
          overflow: 'hidden',
        })
      ),
      state(
        'expanded',
        style({
          height: '*',
          opacity: 1,
        })
      ),
      transition('collapsed => expanded', [animate('300ms ease-out')]),
      transition('expanded => collapsed', [animate('300ms ease-in')]),
    ]),
  ],
  providers: [DecimalPipe],
})
export class ApiKeyComponent implements OnInit {
  /** Subject for clean subscription management */
  private destroy$ = new Subject<void>();

  /** Main list of API key data summary */
  listOfData: listApiKeyData[] = [];

  /** Detailed list for Shipping Cost API */
  listOfCost: ApiKeyDataList[] = [];

  /** Detailed list for Shipping Delivery API */
  listOfDelivery: ApiKeyDataList[] = [];

  /** Detailed list for Payment API */
  listOfPayment: ApiKeyDataList[] = [];

  /** Detailed list for QRISLY API */
  listOfQrisly: ApiKeyDataList[] = [];

  /** Expansion state for Shipping Cost section */
  expandSC: boolean = false;

  /** Expansion state for Shipping Delivery section */
  expandSD: boolean = false;

  /** Expansion state for Payment API section */
  expandPayment: boolean = false;

  /** Expansion state for QRISLY API section */
  expandQrisly: boolean = false;

  /** Name of the application passed from parent component */
  @Input() appName: string = '';

  /** Visible API keys for different services */
  apiKeyCost: string = '';
  apiKeyDelivery: string = '';
  apiKeyPayment: string = '';
  apiKeyQrisly: string = '';

  /** Role level for conditional rendering (e.g., restricted access for level 3) */
  roLevel: string = '';

  /** Loading state for skeleton display */
  loading: boolean = true;

  /** Temporary storage for primary cost data filtered from list */
  private listMainCost: listApiKeyData[] = [];

  constructor(
    private message: NzMessageService,
    config: NgbModalConfig,
    private modalService: NgbModal,
    private baseService: BaseService,
    private clipboard: Clipboard,
    private httpClient: HttpClient,
    private profileService: ProfileService
  ) {}

  /**
   * Initializes component state and starts data fetching chains.
   */
  ngOnInit(): void {
    this.roLevel = String(this.profileService.getRoLevel());
    this.profileService.getRoLevel$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((lvl) => {
        this.roLevel = String(lvl);
      });
    this.getListDataCost();
    setTimeout(() => {
      this.loading = false;
    }, 1000);
  }

  /**
   * Cleans up subscriptions to prevent memory leaks.
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Opens a modal to verify user before showing the API Key.
   * @param serviceType The type of service requesting key visibility ('cost', 'delivery', etc.)
   */
  showKey(serviceType: string) {
    const modal = this.modalService.open(ModalViewKeyComponent, {
      size: 'md',
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    const modalInstance = modal.componentInstance as ModalViewKeyComponent;

    modalInstance.showKey.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (serviceType === 'cost') {
        this.getKeyCost();
      } else {
        this.getKeyDelivery(serviceType);
      }
    });
  }

  /**
   * Copies the given API Key to the clipboard.
   * @param value The value to copy
   */
  copyValue(value: string) {
    this.clipboard.copy(value);
    this.message.create('success', 'Successfully copied API Key');
  }

  /**
   * Resets the visible API Key for a specific service.
   * @param serviceType The service to hide ('cost', 'delivery', 'payment', 'qrisly')
   */
  hideKey(serviceType: string) {
    if (serviceType === 'cost') this.apiKeyCost = '';
    else if (serviceType === 'delivery') this.apiKeyDelivery = '';
    else if (serviceType === 'payment') this.apiKeyPayment = '';
    else if (serviceType === 'qrisly') this.apiKeyQrisly = '';
  }

  /**
   * Formats the API key with visual masking (e.g. bh8n4280......pbv).
   * Displays the first 8 characters, 6 dots, and the last 3 characters.
   * @param serviceType The service identifier ('cost', 'delivery', 'payment', 'qrisly')
   */
  getMaskedKey(serviceType: string): string {
    const key = this.getApiKey(serviceType);
    if (!key) return '';
    if (key.length <= 11) {
      return key.length > 6
        ? `${key.slice(0, 3)}......${key.slice(-3)}`
        : key;
    }
    return `${key.slice(0, 8)}......${key.slice(-3)}`;
  }

  /**
   * Fetches the cost-related API key list using direct HttpClient.
   * This is filtered to extract 'cost' item for the main summary.
   */
  getListDataCost() {
    const baseUrl = environment.SWITCH_URL;
    const endPoint = '/user/api/v1/user/api-key';
    const apiUrl = baseUrl + endPoint;
    const token = localStorage.getItem('auth-token');

    const headers = new HttpHeaders({
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    });

    const context = new HttpContext().set(BypassInterceptorContext, true);

    this.httpClient
      .get<ApiResponse<listApiKeyData[]>>(apiUrl, { headers, context })
      .subscribe({
        next: (res) => {
          this.listMainCost = res.data.filter((item: listApiKeyData) => item.api_name === 'cost');
          this.getListData();
        },
        error: () => this.message.create('error', 'Failed to get list Api Key'),
      });
  }

  /**
   * Fetches the comprehensive list of API keys and populates the data table.
   */
  getListData() {
    this.baseService
      .getData(LIST_APIKEY)
      .pipe(
        takeUntil(this.destroy$),
        map((res: ApiResponse<listApiKeyData[]>) => res.data)
      )
      .subscribe({
        next: (data) => {
          const deliveryList = data.filter((item) => item.api_name === 'delivery');
          const paymentList = data.filter((item) => item.api_name === 'payment');
          const qrislyList = data.filter((item) => item.api_name === 'qrisly');

          this.listOfData = [...(this.listMainCost || []), ...deliveryList, ...paymentList, ...qrislyList].filter(
            data => data.api_name !== 'delivery' || this.roLevel === '3'
          );

          this.fetchServiceCost();
          if (deliveryList.length > 0 && this.roLevel === '3') this.fetchServiceDetails(LIST_APIKEY_DELIVERY, 'delivery');
          if (paymentList.length > 0) this.fetchServiceDetails(LIST_APIKEY_PAYMENT, 'payment');
          if (qrislyList.length > 0) this.fetchServiceDetails(QRISLY_API_LIST, 'qrisly');
        },
        error: () => this.message.create('error', 'Failed to get list Api Key'),
      });
  }

  private fetchServiceCost() {
    const baseUrl = environment.SWITCH_URL;
    const endPoint = '/user/api/v1/limit/user/cost';
    const apiUrl = baseUrl + endPoint;
    const token = localStorage.getItem('auth-token');

    const headers = new HttpHeaders({
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    });

    const context = new HttpContext().set(BypassInterceptorContext, true);

    this.httpClient
      .get<ApiResponse<ApiKeyDataList[]>>(apiUrl, { headers, context })
      .subscribe({
        next: (data) => {
          this.listOfCost = data.data;
        },
        error: () => this.message.create('error', 'Failed to get list Api Key'),
      });
  }

  /**
   * Internal helper to fetch nested details for a specific API service.
   * @param endpoint The API endpoint to hit
   * @param serviceType The component service identifier to store results in
   */
  private fetchServiceDetails(endpoint: string, serviceType: string) {
    this.baseService
      .getData(endpoint)
      .pipe(
        takeUntil(this.destroy$),
        map((res: ApiResponse<ApiKeyDataList[]>) => res.data)
      )
      .subscribe({
        next: (data) => {
          if (serviceType === 'cost') this.listOfCost = data;
          else if (serviceType === 'delivery') this.listOfDelivery = data;
          else if (serviceType === 'payment') this.listOfPayment = data;
          else if (serviceType === 'qrisly') this.listOfQrisly = data;
        },
        error: () => this.message.create('error', 'Failed to get list Api Key'),
      });
  }

  /**
   * Fetches the actual API key string for Shipping Cost.
   */
  getKeyCost() {
    this.baseService
      .getData(SHOW_APICOST)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ApiResponse<{ api_key: string }>) => {
          this.message.create('success', 'Successfully open API Key');
          this.apiKeyCost = res.data.api_key;
        },
        error: (err: { error: { meta: { message: string } } }) => this.message.create('error', err.error.meta.message),
      });
  }

  /**
   * Fetches the actual API key string for a supported service (Delivery, Payment, or QRISLY).
   * @param serviceType The internal service identifier
   */
  getKeyDelivery(serviceType: string) {
    this.baseService
      .getData(SHOW_APIDELIVERY)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ApiResponse<{ api_key: string }>) => {
          this.message.create('success', 'Successfully open API Key');
          const value = res.data.api_key;
          if (serviceType === 'delivery') this.apiKeyDelivery = value;
          else if (serviceType === 'payment') this.apiKeyPayment = value;
          else if (serviceType === 'qrisly') this.apiKeyQrisly = value;
        },
        error: (err: { error: { meta: { message: string } } }) => this.message.create('error', err.error.meta.message),
      });
  }

  /**
   * Maps an HTTP method string to a CSS class name.
   * @param method The HTTP method (GET, POST, etc.)
   */
  classMethod(method: string): string {
    const methodMap: { [key: string]: string } = {
      GET: 'get',
      POST: 'post',
    };
    return methodMap[method] || 'put';
  }

  /**
   * Maps technical API internal names to user-friendly labels.
   * @param technicalName The raw name from the API
   */
  apiName(technicalName: string): string {
    const nameMap: { [key: string]: string } = {
      internationalDestination: 'International Destination',
      internationalOrigin: 'International Origin',
      internationalCost: 'International Cost',
      request: 'Pickup',
    };
    return nameMap[technicalName] || technicalName;
  }

  /**
   * Returns the user-friendly label for a given API service type.
   * @param apiName The internal service identifier ('cost', 'delivery', etc.)
   */
  getApiLabel(apiName: string): string {
    const labelMap: { [key: string]: string } = {
      cost: 'Shipping Cost',
      delivery: 'Shipping Delivery',
      payment: 'Payment API',
      qrisly: 'QRISLY API',
    };
    return labelMap[apiName] || apiName;
  }

  /**
   * Determines if a service row is currently expanded in the UI.
   * @param apiName The internal service identifier
   */
  isExpanded(apiName: string): boolean {
    const expansionMap: { [key: string]: boolean } = {
      cost: this.expandSC,
      delivery: this.expandSD,
      payment: this.expandPayment,
      qrisly: this.expandQrisly,
    };
    return expansionMap[apiName] || false;
  }

  /**
   * Toggles the expansion state of a service row.
   * @param apiName The internal service identifier
   */
  toggleExpand(apiName: string): void {
    if (apiName === 'cost') this.expandSC = !this.expandSC;
    else if (apiName === 'delivery') this.expandSD = !this.expandSD;
    else if (apiName === 'payment') this.expandPayment = !this.expandPayment;
    else if (apiName === 'qrisly') this.expandQrisly = !this.expandQrisly;
  }

  /**
   * Retrieves the currently visible API key for a service.
   * @param apiName The internal service identifier
   */
  getApiKey(apiName: string): string {
    const keyMap: { [key: string]: string } = {
      cost: this.apiKeyCost,
      delivery: this.apiKeyDelivery,
      payment: this.apiKeyPayment,
      qrisly: this.apiKeyQrisly,
    };
    return keyMap[apiName] || '';
  }

  /**
   * Retrieves the detailed hits list for a service.
   * @param apiName The internal service identifier
   */
  getDetailsList(apiName: string): ApiKeyDataList[] {
    const detailMap: { [key: string]: ApiKeyDataList[] } = {
      cost: this.listOfCost,
      delivery: this.listOfDelivery,
      payment: this.listOfPayment,
      qrisly: this.listOfQrisly,
    };
    return detailMap[apiName] || [];
  }
}
